import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@studio/common/services/environment.service';
import {
    EraseRequest,
    GenerateImageRequest,
    ImageFormat,
    InpaintRequest,
    OutpaintImageRequest,
    RemoveBackgroundRequest,
    SearchAndReplaceRequest,
    SketchRequest,
    StructureRequest,
    StyleRequest,
    UpscaleRequest
} from '@studio/domain/api/gen-ai';
import { OutpaintSettings } from '@studio/domain/components/gen-ai/outpaint.types';
import { Observable } from 'rxjs';
import { cleanOutpaintRequest, handlePostResponse } from '../utils/request.utils';

@Injectable({
    providedIn: 'root'
})
export class GenAIDataService {
    private environmentService = inject(EnvironmentService);
    private httpClient = inject(HttpClient);

    private apiRoute = `${this.environmentService.origins.genAiImage}/api/image`;

    generateImage(prompt: string): Observable<string> {
        const url = `${this.apiRoute}/generate`;
        const body: GenerateImageRequest = {
            prompt,
            outputImageFormat: ImageFormat.JPG
        };
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    removeBackground(imageInBase64: string): Observable<string> {
        const url = `${this.apiRoute}/remove-background`;
        const body: RemoveBackgroundRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.PNG
        };

        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    inpaint({
        prompt,
        negativePrompt,
        imageInBase64,
        imageMask
    }: {
        prompt: string;
        negativePrompt?: string;
        imageInBase64: string;
        imageMask: string;
    }): Observable<string> {
        const body: InpaintRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.JPG,
            imageMask,
            negativePrompt,
            prompt
        };
        const url = `${this.apiRoute}/inpaint`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    sketch({
        prompt,
        negativePrompt,
        imageInBase64,
        controlStrength
    }: {
        prompt: string;
        negativePrompt?: string;
        imageInBase64: string;
        controlStrength?: number;
    }): Observable<string> {
        const body: SketchRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.JPG,
            negativePrompt,
            prompt,
            controlStrength
        };
        const url = `${this.apiRoute}/sketch`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    structure({
        prompt,
        negativePrompt,
        imageInBase64,
        controlStrength
    }: {
        prompt: string;
        negativePrompt?: string;
        imageInBase64: string;
        controlStrength?: number;
    }): Observable<string> {
        const body: StructureRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.JPG,
            negativePrompt,
            prompt,
            controlStrength: controlStrength ? controlStrength / 10 : 0
        };
        const url = `${this.apiRoute}/structure`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    style({
        prompt,
        negativePrompt,
        imageInBase64,
        fidelity
    }: {
        prompt: string;
        negativePrompt?: string;
        imageInBase64: string;
        fidelity?: number;
    }): Observable<string> {
        const body: StyleRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.JPG,
            negativePrompt,
            prompt,
            fidelity: fidelity ? fidelity / 10 : 0
        };
        const url = `${this.apiRoute}/style`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    upscale(imageInBase64: string): Observable<string> {
        const body: UpscaleRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.JPG
        };
        const url = `${this.apiRoute}/upscale-fast`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    erase({
        imageInBase64,
        imageMask
    }: {
        imageInBase64: string;
        imageMask: string;
    }): Observable<string> {
        const body: EraseRequest = {
            imageUrlOrBase64: imageInBase64,
            outputImageFormat: ImageFormat.JPG,
            imageMask
        };
        const url = `${this.apiRoute}/erase`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    outpaint(
        imageInBase64: string,
        settigns: OutpaintSettings,
        prompt?: string,
        negativePrompt?: string
    ): Observable<string> {
        const body: OutpaintImageRequest = cleanOutpaintRequest({
            ...settigns,
            outputImageFormat: ImageFormat.JPG,
            prompt,
            negativePrompt,
            imageUrlOrBase64: imageInBase64
        });

        const url = `${this.apiRoute}/outpaint`;
        return this.httpClient.post(url, body, { responseType: 'blob' }).pipe(handlePostResponse);
    }

    searchAndReplace(
        searchPrompt: string,
        replacePrompt: string,
        imageUrlOrBase64: string
    ): Observable<string> {
        const body: SearchAndReplaceRequest = {
            imageUrlOrBase64,
            outputImageFormat: ImageFormat.JPG,
            prompt: replacePrompt,
            searchPrompt
        };

        return this.httpClient
            .post(`${this.apiRoute}/search-and-replace`, body, { responseType: 'blob' })
            .pipe(handlePostResponse);
    }
}
